import { BarChart3, BookOpenText, ChevronDown, CircleDollarSign, FileSpreadsheet, LogOut, PackageSearch, Settings2, Split, UsersRound } from "lucide-react";
import type { MouseEvent, ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { api } from "../api";
import { useAppContext } from "../context";
import { roleLabels } from "../format";

const navigation = [
  { to: "/dashboard", label: "利润看板", icon: BarChart3 },
  { to: "/orders", label: "订单利润", icon: PackageSearch },
  { to: "/expenses", label: "费用分摊", icon: Split },
  { to: "/creators", label: "TikTok达人", icon: UsersRound },
  { to: "/imports", label: "导入导出", icon: FileSpreadsheet },
  { to: "/quality", label: "数据质量", icon: CircleDollarSign }
];

export function AppShell({ children }: { children: ReactNode }) {
  const {
    bootstrap, storeId, platformFilter, siteFilter, month, periodGranularity, periodAnchor, reportingCurrency, profitMode,
    setStoreId, setPlatformFilter, setSiteFilter, setMonth, setPeriodGranularity, setPeriodAnchor, setReportingCurrency, setProfitMode, confirmDiscardChanges
  } = useAppContext();
  const platformStores = bootstrap.stores.filter((store) => !platformFilter || store.platform_code === platformFilter);
  const sites = [...new Set(platformStores.map((store) => store.site))].sort();
  const availableStores = platformStores.filter((store) => !siteFilter || store.site === siteFilter);

  async function logout() {
    if (!confirmDiscardChanges()) return;
    await api<void>("/api/auth/logout", { method: "POST" });
    window.location.assign("/");
  }

  function guardLink(event: MouseEvent<HTMLAnchorElement>) {
    if (!confirmDiscardChanges()) event.preventDefault();
  }

  return <div className="profit-shell">
    <aside className="profit-sidebar">
      <NavLink className="profit-brand" to="/dashboard" onClick={guardLink}>
        <span><BookOpenText size={19} /></span>
        <div><strong>清账</strong><small>跨境利润账本</small></div>
      </NavLink>
      <nav aria-label="利润核算导航">
        {navigation.map(({ to, label, icon: Icon }) => <NavLink key={to} to={to} onClick={guardLink} className={({ isActive }) => isActive ? "active" : ""}><Icon size={17} /><span>{label}</span></NavLink>)}
      </nav>
      <div className="sidebar-spacer" />
      {bootstrap.user.role === "admin" ? <NavLink to="/settings" onClick={guardLink} className={({ isActive }) => isActive ? "active" : ""}><Settings2 size={17} /><span>基础设置</span></NavLink> : null}
      <div className="sidebar-user">
        <span>{bootstrap.user.name.slice(0, 1)}</span>
        <div><strong>{bootstrap.user.name}</strong><small>{roleLabels[bootstrap.user.role]}</small></div>
        <button className="icon-button" aria-label="退出登录" onClick={() => void logout()}><LogOut size={17} /></button>
      </div>
    </aside>
    <section className="profit-workspace">
      <header className="profit-toolbar">
        <div className="context-selectors profit-context">
          <label className="platform-control"><span className="sr-only">平台</span><select value={platformFilter} onChange={(event) => { const next = event.target.value as typeof platformFilter; setPlatformFilter(next); setSiteFilter(""); setStoreId(""); }}><option value="">全部平台</option><option value="amazon">Amazon</option><option value="tiktok">TikTok</option></select><ChevronDown size={14} /></label>
          <label className="site-control"><span className="sr-only">国家站点</span><select value={siteFilter} onChange={(event) => { setSiteFilter(event.target.value); setStoreId(""); }}><option value="">全部站点</option>{sites.map((site) => <option key={site}>{site}</option>)}</select><ChevronDown size={14} /></label>
          <label><span className="sr-only">店铺</span><select value={storeId} onChange={(event) => confirmDiscardChanges() && setStoreId(event.target.value)}><option value="">全部店铺</option>{availableStores.map((store) => <option key={store.id} value={store.id}>{store.platform} · {store.name}</option>)}</select><ChevronDown size={15} /></label>
          <label className="granularity-control"><span className="sr-only">周期</span><select value={periodGranularity} onChange={(event) => setPeriodGranularity(event.target.value as typeof periodGranularity)}><option value="day">日</option><option value="week">周</option><option value="month">月</option><option value="quarter">季度</option><option value="year">年度</option></select><ChevronDown size={14} /></label>
          <label className="month-control"><span className="month-label">期间</span>{periodGranularity === "day" || periodGranularity === "week" ? <input aria-label="核算日期" type="date" value={periodAnchor} onChange={(event) => confirmDiscardChanges() && setPeriodAnchor(event.target.value)} /> : <input aria-label="核算月份" type="month" value={month} onChange={(event) => confirmDiscardChanges() && setMonth(event.target.value)} />}</label>
        </div>
        <div className="toolbar-segment" aria-label="利润口径">
          <button className={profitMode === "estimated" ? "active" : ""} onClick={() => setProfitMode("estimated")}>预估</button>
          <button className={profitMode === "actual" ? "active" : ""} onClick={() => setProfitMode("actual")}>实际</button>
          <button className={profitMode === "variance" ? "active" : ""} onClick={() => setProfitMode("variance")}>差异</button>
        </div>
        <div className="toolbar-segment" aria-label="报表币种">
          <button className={reportingCurrency === "CNY" ? "active" : ""} onClick={() => setReportingCurrency("CNY")}>CNY</button>
          <button className={reportingCurrency === "USD" ? "active" : ""} onClick={() => setReportingCurrency("USD")}>USD</button>
        </div>
      </header>
      <main className="profit-content">{children}</main>
    </section>
  </div>;
}
